import React, { forwardRef, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { COLORS, TYPOGRAPHY } from '@app/constants';

import { TherapistSearchResponseDto } from '@app/api';
import { BreakpointsEnum } from '@app/types';
import { DateUtil } from '@app/utils/dateUtility';
import { useImagePreload } from '@app/hooks/useImagePreload';
import {
  Lightning,
  Play,
  TopSpecialist,
} from '@app/ui/iconComponents';
import ButtonLink from '../ButtonLink';
import Button from '../Button';
import { useSignupClickHandler } from '@app/hooks/useSignUpHandler';
import Modal from '../Modal';
import EnrollModal from '@app/components/EnrollModal';
import { useRouter } from 'next/router';
import { useClientSize } from '@app/hooks';
import { useState } from 'react';
import BasicModal from '@/landing/ui/components/BasicModal';
import apx from '@/landing/constants/adaptivepx';
import { useEffect } from 'react';
import { sendMetrik } from '@/app/utils/sendMetrik';


interface Props {
  data: TherapistSearchResponseDto;
  className?: string;
  isSmall?: boolean;
  onClickCard?: () => void;
  childTherapist?: boolean;
}

const SpecialistCard = forwardRef<HTMLDivElement, Props>(
  ({ data, isSmall, className, childTherapist }, ref) => {
    const router = useRouter();
    const preloadImage = useImagePreload();
    const { getIsBreakpoint } = useClientSize();
    const isMobile = getIsBreakpoint('sm');

    const {
      handleClickSignupButton,
      isEnrollModalOpen,
      toggleEnrollModalOpen,
    } = useSignupClickHandler();

    const handleChangeAvailability = (availabilityId: string) => {
      toggleEnrollModalOpen();
      router.push(`/session-payment?availabilityId=${availabilityId}`);
    };

    const therapist = useMemo(
      () => ({
        id: data.id,
        firstname: data.firstname || '',
        lastname: data.lastname || '',
        avatarKey: data.avatarKey || '',
      }),
      [data],
    );

    const isToday = DateUtil.now(
      data.closestFreeAvailability.startDate,
    ).isToday();
    const isTomorrow = DateUtil.now(
      data.closestFreeAvailability.startDate,
    ).isTomorrow();

    const formattedStartDate = isToday
      ? `Сегодня, ${DateUtil.now(data.closestFreeAvailability.startDate).format(
          'HH:00',
        )}`
      : isTomorrow
      ? `Завтра, ${DateUtil.now(data.closestFreeAvailability.startDate).format(
          'HH:00',
        )}`
      : DateUtil.now(data.closestFreeAvailability.startDate).format(
          'DD MMMM, HH:00',
        );

    const previewImageKey = data.avatarKey; // data.miniAvatarKey || data.avatarKey <- miniAvatarKey is updated immediately when the avatar is changed by a specialist, but should be updated only after approval by the moderator

    const preloadAvatar = () => preloadImage(data.avatarKey);

    const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;

      if (
        target.tagName !== 'A' &&
        target.closest('button') === null &&
        target.closest('a') === null
      ) {
        router.push({
          pathname: '/therapist/[url]',
          query: {
            url: data.url,
            tab: childTherapist && 'child'
          },
        });
      }
    };

  const [videoIsOpen, setVideoIsOpen] = useState(false);
  const [modalContent, setModalContent] = useState<JSX.Element>();
  const videoRef = useRef<HTMLVideoElement>(null);

  const videoPoster = isMobile
    ? '/assets/images/main-preview.jpg'
    : '';

  useEffect(() => {

    setModalContent(

      <ModalVideo
        ref = { videoRef }
        controls
        src = { data.videoKey }
        onEnded = { () => setVideoIsOpen(false) }
        poster = { videoPoster }
      />

    );

    if ( !videoIsOpen && videoRef.current ) {

      videoRef.current.pause();
      videoRef.current.currentTime = 0;

    }

  }, [ videoIsOpen, data.videoKey, videoPoster ]);


    return (
      <Root
        className={className}
        ref={ref}
        $isSmall={isSmall}
        onMouseEnter={preloadAvatar}
        onTouchStart={preloadAvatar}
      >
        <MainBlock onClick={handleCardClick}>
          <PhotoWrap isTop={data.isTop}>
            {previewImageKey && (
              <Img src={previewImageKey} alt={data.firstname} />
            )}
            {!!data?.isTop && (
              <TopSpecialistWrap>
                <TopSpecialist />
              </TopSpecialistWrap>
            )}
          </PhotoWrap>
          <UserName>
            {data.firstname} {data.lastname}
          </UserName>

          {data.price && (
            <InfoLabel>
              {/* {age && `${makeFriendlyAge(age)},`}  */}
              {data.price} ₽
            </InfoLabel>
          )}

          <AchievementsWrap>
            {data.totalSessions > 0 && (
              <AchievementTag>
                <Lightning color={COLORS.primary300} />
                <span>{data.totalSessions}+ сессий</span>
              </AchievementTag>
            )}
          </AchievementsWrap>

            { data.videoKey && (

              <VideoLinkWrap>

                <VideoLink

                  onClick={() => {

                    setVideoIsOpen(true);

                  }}

                >
                  <Play color = { COLORS.white } width = { 17 } height = { 17 } />

                  <span>Посмотреть видео-визитку</span>

                </VideoLink>

              </VideoLinkWrap>

            ) }

          <AvailableTimeWrap>
            <CardSubtitle>Ближайшее время -</CardSubtitle>
            <AvailableTime>
              {data?.closestFreeAvailability?.startDate
                ? formattedStartDate
                : 'По запросу'}
            </AvailableTime>
          </AvailableTimeWrap>
        </MainBlock>

        <Divider color={COLORS.grayscale200} />

        {data.about.length > 0 && (
          <InfoBlock onClick={handleCardClick}>
            <Title>О себе</Title>
            <Description>
              {/* {`${data.about.split(' ').slice(0, 18).join(' ')}...`} */}
              {data.about}
            </Description>
            {/* <Title>Помогаю с запросами</Title>
          <Ul>
            {diseaseGroups.map((item, index) => (
              <List key={index}>{item}</List>
            ))}
          </Ul> */}
          </InfoBlock>
        )}

        <ActionsBlock>
          <LinkButton
            href={undefined}
            onClick={() => {router.push({
              pathname: '/therapist/[url]',
              query: {
                url: data.url,
                tab: childTherapist && 'child'
              },
            })}}
          >
            <span>Подробнее о специалисте</span>
          </LinkButton>

          <BookButton
            styleType="primary"
            onClick={() => {
              sendMetrik('reachGoal', 'child_booking_time_click');

              if (data?.closestFreeAvailability?.startDate) {
                handleClickSignupButton();
              } else {
                toggleEnrollModalOpen();
              }
            }}
          >
            <span>Забронировать время</span>
          </BookButton>
        </ActionsBlock>

        {/* {isMobile && (
          <VideoBlock>
            <StyledVideoPlayer
              blur={true}
              autoplayWhenInView
              src="https://storage.yandexcloud.net/stage-pogovorim.ru/users/0bcb3f22-d1bb-433d-98d9-180e8822ffbd/videos/0df04532-cfe5-4a26-b9fd-482318db7715.mp4"
            />
          </VideoBlock>
        )} */}

        <StyledEnrollModal
          isOpen={isEnrollModalOpen}
          onClose={toggleEnrollModalOpen}
          withoutHeader
        >
          <EnrollModal
            onSubmitEnroll={handleChangeAvailability}
            onCancel={toggleEnrollModalOpen}
            therapist={therapist}
          />
        </StyledEnrollModal>

        <BasicModal

          backgroundColor="transparent"
          visible = { videoIsOpen }
          setVisible = { setVideoIsOpen }
          padding = {`${ apx.d( 40 ) }`}
          mobilePadding = {`${ apx.m( 40 ) }`}

        >

          <ModalContent>

           {modalContent}

          </ModalContent>

        </BasicModal>

      </Root>
    );
  },
);

SpecialistCard.displayName = 'SpecialistCard';

export default SpecialistCard;

interface StyleProps {
  $isSmall?: boolean;
}

interface PhotoWrapProps {
  isTop: boolean;
}

const Root = styled.div<StyleProps>`
  width: 100%;
  height: fit-content;
  max-width: 860px;
  background: ${COLORS.white};
  box-shadow: 0px 0px 29px 0px rgba(170, 170, 170, 0.1);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;

  &:hover {
    box-shadow: 0 0 29px rgba(170, 170, 170, 0.3);
  }

  @media (max-width: ${BreakpointsEnum.md}px) {
    display: grid;
    grid-template-columns: ${({ $isSmall }) => ($isSmall ? '1fr' : '1fr 2fr')};
    height: 100%;
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    display: grid;
    grid-template-columns: 1fr;
    max-width: 100%;
    box-shadow: 0px 0px 29px rgba(170, 170, 170, 0.2);
    border-radius: 24px;
    padding: 16px 16px 20px 16px;
  }
`;

const VideoLink = styled.a`
  display: flex;
  align-items: center;
  width: fit-content;
  ${TYPOGRAPHY.body1Regular13};
  font-size: 11px;
  color: ${COLORS.white};
  background-color: ${COLORS.primary300};
  padding: 7px 10px;
  border-radius: 14px;
  cursor: pointer;
  height: 30px;

  span {
    margin-left: 10px;
    line-height: 15px;
  }

  &:hover {
    background-color: ${COLORS.primary400};
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    height: 30px;
    font-size: 13px;
  }
`;

const VideoLinkWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 13px;
  margin-bottom: 13px;
`;

const Divider = styled.hr`
  width: 85%;
  height: 1px;
  border: none;
  margin-top: 13px;
  margin-bottom: 13px;
`;

const Title = styled.div`
  margin-bottom: 5px;
  ${TYPOGRAPHY.disclaimerMedium12};
  color: #b3b3b3;
`;

const LinkButton = styled(ButtonLink)`
  width: 100%;
  padding: 14px 16px;
  ${TYPOGRAPHY.footnoteRegular13};
  margin-bottom: 10px;
  color: ${COLORS.grayscale700};
  cursor: pointer;
  border: 1px solid ${COLORS.elementsStates};
  background-color: ${COLORS.white};
`;

const BookButton = styled(Button)`
  width: 100%;
  padding: 15px 16px;
  ${TYPOGRAPHY.body1Semibold14};
`;

const AchievementsWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 7px;
  margin-bottom: 7px;
`;

const AchievementTag = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 10px;
  height: 30px;
  background-color: #f6f6f5;
  color: ${COLORS.primary300};
  border-radius: 16px;
  margin-right: 7px;

  &:last-child {
    margin-right: 0;
  }

  span {
    margin-left: 8px;
    ${TYPOGRAPHY.disclaimerRegular12};
    font-size: 11px;
    color: ${COLORS.gray1};
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    height: 30px;
    padding: 7px 15px;

    span {
      margin-left: 10px;
      ${TYPOGRAPHY.body1Regular13};
      color: ${COLORS.gray1};
    }
  }
`;

const RecommendTag = styled.div`
  display: flex;
  align-items: center;
  height: 30px;
  padding: 5px 10px;
  background-color: ${COLORS.primary50};
  color: ${COLORS.primary300};
  border-radius: 16px;

  span {
    margin-left: 8px;
    ${TYPOGRAPHY.footnoteMedium13};
    font-size: 11px;
    line-height: 11px;
    color: ${COLORS.primary300};
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
    padding: 7px 15px;
    height: 30px;

    span {
      font-size: 13px;
    }
  }
`;

const MainBlock = styled.div`
  width: 100%;
`;

const ActionsBlock = styled.div`
  padding: 0 16px 16px;

  @media (max-width: ${BreakpointsEnum.sm}px) {
    padding: 0;
  }
`;

const InfoLabel = styled.div`
  ${TYPOGRAPHY.body1Regular14};
  color: ${COLORS.grayscale600};
  text-align: center;
`;

const AvailableTimeWrap = styled.div`
  display: flex;
  justify-content: center;
`;

const UserName = styled.div`
  text-align: center;
  margin-top: 200px;
  ${TYPOGRAPHY.heading4Semibold20};
  color: ${COLORS.black};

  @media (max-width: ${BreakpointsEnum.sm}px) {
    margin-top: 190px;
    margin-bottom: 4px;
  }
`;

const Description = styled.div`
  ${TYPOGRAPHY.disclaimerRegular12};
  color: ${COLORS.gray1};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PhotoWrap = styled.div<PhotoWrapProps>`
  width: 100%;
  max-width: 260px;

  display: flex;
  align-items: center;
  position: absolute;
  top: -30px;
  left: calc(50% - 110px);

  img {
    width: 220px;
    border-radius: 16px;
    border: ${(props) => (props.isTop ? `2px solid ${COLORS.isTop}` : 'none')};
  }

  @media (max-width: ${BreakpointsEnum.sm}px) {
  }
`;

const TopSpecialistWrap = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
`;

const Img = styled.img`
  width: 100%;
  object-fit: cover;
  display: block;
`;

const InfoBlock = styled.div`
  width: 100%;
  padding: 0px 22px;
  margin-bottom: 13px;
  min-height: 70px;
`;

const CardSubtitle = styled.p`
  ${TYPOGRAPHY.footnoteMedium13};
  color: #888;
`;

const AvailableTime = styled.p`
  ${TYPOGRAPHY.footnoteMedium13};
  font-weight: 500;
  color: ${COLORS.black};
  margin-left: 3px;
`;

const StyledEnrollModal = styled(Modal)`
  @media (min-width: ${BreakpointsEnum.sm}px) {
    max-width: 716px;
  }
`;

const ModalContent = styled.div`
  width: ${ apx.d( 400 ) };

  @media screen and ( max-width: ${BreakpointsEnum.md}px) {
    width: ${ apx.m( 120 ) };
  }

  @media screen and ( max-width: ${BreakpointsEnum.s}px) {
    width: ${ apx.m( 220 ) };
  }
`;

const ModalVideo = styled.video`
  border-radius: ${ apx.d( 30 ) };

  @media screen and ( max-width: ${BreakpointsEnum.md}px) {
    border-radius: ${ apx.m( 10 ) };
  }

  @media screen and ( max-width: ${BreakpointsEnum.s}px) {
    border-radius: ${ apx.m( 20 ) };
  }
`;

